/**
 * 史実イベント — 第II章「群雄割拠」（1521-1559）
 *
 * docs/01-ゲームデザイン.md §7 の第二章の表に対応する。
 * 表にある5件（鉄砲伝来・キリスト教伝来・川中島・厳島・桶狭間）に加え、
 * 合戦以外の出来事（銀の精錬技術・分国法・寺内町と一揆・交易の断絶）を
 * 4件足して9件としてある。docs/05-学習設計.md §4-4 に従い、
 * 章の内容が合戦だけに偏らないようにするためである。
 *
 * 【この章の設計方針】
 * 第II章は本作の学習設計の要である。1543年の鉄砲伝来によって
 * 軍札が 52枚 → 65枚 に書き換わり、手役の出やすさそのものが変わる
 * （docs/06-賭けと軍配勝負.md §2）。
 * 「1543年に鉄砲が伝わった」を暗記するのではなく、
 * 戦場札に鉄砲が並び始める光景として覚えてもらう。
 * したがって各イベントの effects は、資源の増減で終わらせず、
 * できる限り「山札・手勢札・役・盤上の位置」を書き換える形にしている。
 *
 * 【執筆基準】docs/05-学習設計.md §4 と provinces/_reference.ts に揃える。
 *   - 武将を「英雄」「悪」と評価しない。戦闘行為を美化・賛美しない
 *   - 宗教勢力（一向一揆・法華一揆・キリスト教）は事実の記述に留める
 *   - 女性・民衆の存在を消さない。合戦以外の生活面の事実を必ず入れる
 *   - 通説と異説がある事項は caveat に必ず書く
 *   - ゲームの都合で史実を改変した箇所は gameNote に必ず書く
 *   - 年号は西暦を主とし、必要に応じて和暦を添える
 *
 * 【章の区切りについて】
 * 第II章は 1521-1559 だが、docs/01 §7 の第二章の表には 1560年の桶狭間が
 * 含まれている。章の終わりと第III章の始まりが重なる位置づけなので、
 * 本ファイルでも chapter: 2 の締めくくりとして収めた。
 */

import type { HistoricalEvent } from '../types';

// ---------------------------------------------------------------------------
// 1532 — 山科本願寺の焼失（事件札）
// ---------------------------------------------------------------------------

/**
 * 宗教勢力を「敵」として描かないため、効果は討伐か和睦かの選択にしてある。
 * どちらを選んでも失うものがある、という形にした。
 */
export const YAMASHINA_HONGANJI: HistoricalEvent = {
  id: 'ev:yamashina-honganji',
  year: 1532,
  chapter: 2,
  kind: 'incident',
  name: '山科本願寺の焼失',
  reading: 'やましなほんがんじのしょうしつ',
  effects: [
    {
      t: 'choice',
      options: [
        {
          label: '門徒と和睦し、寺内町の自治を認める（銭を3失う）',
          then: [{ t: 'lose', res: 'zeni', n: 3 }],
        },
        {
          label: '兵を出して寺内町を攻める（兵を3失う）',
          then: [{ t: 'lose', res: 'hei', n: 3 }],
        },
      ],
    },
  ],
  note: {
    summary:
      '1532年、京都の東にあった浄土真宗本願寺の拠点・山科本願寺が、細川晴元方の軍勢と京の法華宗徒の一揆に攻められて焼けた。本願寺は大坂へ移り、のちの石山本願寺となった。',
    detail: {
      1: '1532年、京都の東にあった山科本願寺という大きなお寺が、争いのすえに焼けてしまいました。お寺のまわりには町があり、たくさんの人がくらしていました。本願寺はこのあと大坂にうつり、大きな力を持つようになりました。',
      2: '天文元年(1532)、山城国山科にあった浄土真宗本願寺派の拠点・山科本願寺が、細川晴元方の軍勢と六角定頼、そして京都の法華宗徒による法華一揆に攻められ、寺内町ごと焼失した。山科本願寺は堀と土塁に囲まれ、内部に御影堂や阿弥陀堂だけでなく、商人や職人の住む町（寺内町）を抱えた都市的な拠点だった。焼失後、本願寺は大坂の大坂御坊へ移り、これがのちに石山本願寺と呼ばれる。この時期の畿内では、浄土真宗の門徒による一向一揆と、京都の町衆を担い手とする法華一揆が、それぞれ武装して政治勢力となっていた。',
      3: '天文元年(1532)8月、山城国山科にあった浄土真宗本願寺派の本拠・山科本願寺が、細川晴元・六角定頼の軍勢および京都の法華宗徒による法華一揆の攻撃を受けて焼失した。背景には、細川晴元と細川晴国・三好氏らの畿内の抗争に本願寺門徒が動員されたこと、その門徒勢力の膨張を晴元方が警戒したことがある。山科本願寺は蓮如の時代に築かれ、二重の堀と土塁に囲まれた御本寺・内寺内・外寺内の三重構造を持ち、内部に商工業者の住む寺内町を抱えていた。信仰の拠点であると同時に、市と職人と流通を備えた都市であった点が重要である。焼失後、証如は蓮如が隠居所として整備していた大坂御坊へ移り、これが石山本願寺となる。一方の法華一揆は、京都の町衆が日蓮宗の信仰のもとに自衛と自治を担った組織で、この後1536年の天文法華の乱で延暦寺方の攻撃を受けて京都を追われる。戦国期の畿内では、大名だけでなく寺院・門徒・町衆といった集団が自ら武装し、都市の運営や年貢の交渉を行っていた。合戦の記録の背後には、寺内町で商いをし、機を織り、市に立った男女の生活があったことを見落とさないようにしたい。',
    },
    year: 1532,
    keywords: ['山科本願寺', '寺内町', '法華一揆', '一向一揆', '石山本願寺', '町衆'],
    caveat:
      'この時期の一向一揆・法華一揆は、しばしば「宗教勢力が大名に逆らった」とだけ説明されますが、実際には畿内の政治抗争に各勢力が動員された面が大きく、門徒や町衆の側にも自衛や自治といった事情がありました。どちらか一方を正しい・誤っていると評価できる出来事ではありません。焼失の年は和暦では享禄5年／天文元年にあたり、この年の途中で改元されているため、史料により表記が分かれます。',
    gameNote:
      'ゲームでは「和睦して銭を失う」か「兵を出して兵を失う」かの二択にしていますが、実際の大名と一揆の関係はもっと多様で、和睦・共存・対立が入り混じっていました。二択にしたのは遊びとして判断させるための単純化です。また、この事件は本来は畿内で起きたもので、盤上ではどのプレイヤーが引いても発生します。',
  },
};

// ---------------------------------------------------------------------------
// 1533 — 灰吹法の導入（時代法）
// ---------------------------------------------------------------------------

/**
 * 銀は第II章の経済そのもの。合戦以外の「技術」を章の時代法に置いている。
 * 銭に替えるか兵に替えるかの選択を全員に強いる形にした。
 */
export const HAIFUKIHOU: HistoricalEvent = {
  id: 'ev:haifukihou',
  year: 1533,
  chapter: 2,
  kind: 'era-law',
  name: '灰吹法の導入',
  reading: 'はいふきほうのどうにゅう',
  effects: [
    {
      t: 'target',
      who: 'all',
      then: [
        {
          t: 'choice',
          options: [
            {
              label: '銀を銭に替えて蓄える（銭+3）',
              then: [{ t: 'gain', res: 'zeni', n: 3 }],
            },
            {
              label: '銀で武具と兵糧を買う（兵+3）',
              then: [{ t: 'gain', res: 'hei', n: 3 }],
            },
          ],
        },
      ],
    },
  ],
  note: {
    summary:
      '1533年、石見銀山に灰吹法という銀の精錬技術が持ち込まれ、銀の産出が飛躍的に増えた。日本の銀は明や朝鮮へ流れ、生糸などと交換されて、東アジアの交易を大きく動かした。',
    detail: {
      1: '1533年、石見（いまの島根県）の銀山に、銀をとり出すあたらしいやり方がつたわりました。灰吹法（はいふきほう）といいます。これで銀がたくさんとれるようになり、日本の銀は海をこえて中国へはこばれました。',
      2: '天文2年(1533)、博多の商人神屋寿禎が宗丹・慶寿（けいじゅ）らを石見銀山へ招き、灰吹法による精錬を行った。灰吹法は、銀を含む鉱石に鉛を加えて溶かし、灰を敷いた炉で加熱して鉛を灰に吸わせ、純度の高い銀を取り出す技術で、朝鮮半島を経て伝わったとされる。これにより石見銀山の産出は大きく増え、日本の銀は博多や堺の商人を通じて明や朝鮮へ運ばれ、生糸・絹織物・薬種などと交換された。銀山では掘子（ほりこ）と呼ばれる坑夫のほか、選鉱や炊事に従事する女性を含む多くの人びとが働き、銀山町が形成された。銀の産出は大内氏・尼子氏・毛利氏がこの地を争う理由にもなった。',
      3: '天文2年(1533)、博多商人の神屋寿禎が宗丹・慶寿（けいじゅ）ら技術者を石見銀山（現在の島根県大田市）に招き、灰吹法による銀の精錬を開始した。灰吹法は鉱石に鉛を加えて貴鉛（銀と鉛の合金）を作り、骨灰などを敷いた炉で加熱して鉛を酸化・吸収させ、銀を分離する技術で、朝鮮半島経由で伝えられたとされる。それ以前の日本の銀生産は鉱石をそのまま明へ輸出する段階にとどまっていたが、この技術の導入により純度の高い灰吹銀を国内で得られるようになり、産出量は飛躍的に増加した。16世紀後半には日本産の銀が世界の産出量の相当部分を占めたとする推計もあるが、その割合については研究者により幅がある。銀は博多・堺の商人や、のちにはポルトガル船を介して明へ流れ、生糸・絹・陶磁器と交換された。明では税の銀納化が進んでおり、日本銀はその需要に応える形で吸収された。銀山では坑道（間歩）を掘る掘子、鉱石を選り分ける女性たち、炭を焼く者、米や酒を売る商人が集まり、山中に人口の多い町が生まれている。粉塵による珪肺で若くして亡くなる者が多かったことも記録から知られる。石見銀山の支配権は大内氏・尼子氏・毛利氏の間で繰り返し移り、この争奪が中国地方の政治情勢を規定した。',
    },
    year: 1533,
    keywords: ['石見銀山', '灰吹法', '神屋寿禎', '銀', '日明貿易', '銀山町'],
    caveat:
      '「16世紀の日本が世界の銀産出の3分の1を占めた」という数字がよく引かれますが、これは推計であり、根拠となる統計は残っていません。研究者によって見積もりに幅があります。また灰吹法そのものは古代から各地で知られた技術で、1533年に発明されたのではなく、石見銀山へ本格的に導入された年として伝えられているものです。',
    gameNote:
      'この時代法は全員が「銀を銭に替える／武具と兵糧に替える」を選ぶ形にしていますが、実際には銀山を持つ勢力だけが直接の利益を得ました。全員に適用しているのは、銀の流入が日本全体の物価と交易を動かしたことを遊びに落とし込むためです。石見(30)の銀山そのものの産出は、国マスの yields で別に表現しています。',
  },
};

// ---------------------------------------------------------------------------
// 1543 — 鉄砲伝来（時代法・この章の中核）
// ---------------------------------------------------------------------------

/**
 * 本作の学習設計の中核。
 * deckInject により軍札が 52枚 → 65枚 になり、手役の確率が変わる。
 * docs/06 §2「デッキが時代とともに変わる」を参照。
 */
export const TEPPOU_DENRAI: HistoricalEvent = {
  id: 'ev:teppou-denrai',
  year: 1543,
  chapter: 2,
  kind: 'era-law',
  name: '鉄砲伝来',
  reading: 'てっぽうでんらい',
  effects: [{ t: 'deckInject', arm: 'teppou' }],
  note: {
    summary:
      '1543年、種子島に漂着した中国船に乗っていたポルトガル人が火縄銃を伝えたとされる。鉄砲は堺・国友・根来などで作られるようになり、合戦の戦い方と城の構えを変えていった。',
    detail: {
      1: '1543年、種子島（いまの鹿児島県）に外国の船がながれつき、乗っていたポルトガル人が鉄砲をつたえたといわれます。鉄砲はすぐに日本でも作られるようになり、戦いのしかたが大きくかわりました。',
      2: '天文12年(1543)、大隅国種子島に漂着した中国船に乗っていたポルトガル人が火縄銃をもたらしたと伝えられる。島主の種子島時堯が2挺を買い求め、家臣に製法を学ばせたという。鉄砲はやがて和泉の堺、近江の国友、紀伊の根来・雑賀などで生産されるようになり、数十年のうちに各地の大名が備えるところとなった。鉄砲は訓練の期間が弓より短くて済む一方、火薬の原料である硝石は国内でほとんど採れず、輸入に頼らざるをえなかった。そのため鉄砲を活かせるかどうかは、湊と交易路を押さえているかに左右された。城も、鉄砲に耐えるために土塁や堀を広く取る形へ変わっていく。',
      3: '天文12年(1543)、大隅国種子島に中国船が漂着し、乗り合わせていたポルトガル人が火縄銃（マラッカ経由で伝わった前装式滑腔銃）を伝えたとされる。この経緯は、1600年代初めに薩摩の禅僧南浦文之が記した『鉄炮記』に基づく。同書は事件から60年以上のちの成立であり、ポルトガル側の史料には1542年の到来を示すものもあるため、伝来年については1542年説、1542年に漂着した者が翌1543年に再来したとする説などが並立している。また鉄砲そのものは、それ以前から倭寇の活動を通じて日本に持ち込まれていた可能性も指摘されており、「1543年に初めて鉄砲が日本に入った」と断定はできない。伝来後の普及は速く、堺・国友・根来・雑賀のほか各地の鍛冶が生産に加わった。技術的には、尾栓のねじ切りが当初の難所だったと伝えられる。運用上の制約は火薬にあり、硝石は国内でほとんど産出しないため、明や東南アジアからの輸入に依存した。硝煙の原料を確保できる湊を押さえた勢力が鉄砲を大量に運用できたという関係は、この時期の政治地図を理解する鍵になる。鉄砲の普及は築城にも影響し、横矢掛りや広い横堀を備えた縄張りへの転換が進んだ。なお、鉄砲の登場が合戦を一変させたとする理解は近年やや慎重に扱われており、弓や長槍が引き続き主力であったこと、鉄砲が決定的な役割を果たすのは一定量が揃った後であることが指摘されている。',
    },
    year: 1543,
    keywords: ['鉄砲伝来', '種子島', '火縄銃', '堺', '国友', '硝石'],
    caveat:
      '伝来の年を1543年とするのは『鉄炮記』（1600年代初め成立）に基づく通説で、ポルトガル側の史料をもとにした1542年説、1542年の漂着者が翌年再来したとする説もあります。それ以前に倭寇を通じて鉄砲が持ち込まれていた可能性も指摘されており、「この年に初めて伝わった」と断定はできません。また「鉄砲の登場で合戦が一変した」という説明も、弓や長槍が引き続き主力であったことから、近年は慎重に扱われています。',
    gameNote:
      '盤上ではこの時代法が発動した瞬間に、軍札（合戦の山札）へ鉄砲13枚が加わり、52枚から65枚になります。同じ数字の札が5枚存在するようになるため「無双」が初めて成立し、「方円」も作りやすくなります。これは史実の再現ではなく、時代の変化を確率の変化として体感させるための仕組みです。実際には鉄砲が全国へ行き渡るまでには年月がかかり、硝石の入手や生産の体制が整った勢力から順に使えるようになりました。',
  },
};

// ---------------------------------------------------------------------------
// 1547 — 分国法の広まり（時代法）
// ---------------------------------------------------------------------------

/**
 * 合戦以外の統治の事実を章の柱に据えるための時代法。
 * 「当主が家中の争いを裁く」を、軍略（読みの資源）と手札で表す。
 */
export const EV_BUNKOKUHOU: HistoricalEvent = {
  id: 'ev:bunkokuhou',
  year: 1547,
  chapter: 2,
  kind: 'era-law',
  name: '分国法の広まり',
  reading: 'ぶんこくほうのひろまり',
  effects: [
    {
      t: 'target',
      who: 'all',
      then: [
        { t: 'gain', res: 'gunryaku', n: 1 },
        { t: 'draw', n: 1 },
      ],
    },
  ],
  note: {
    summary:
      '戦国大名が自分の領国だけに通用する法を定めた。伊達氏の塵芥集(1536)、武田氏の甲州法度之次第(1547)、今川仮名目録追加(1553)など。家臣どうしの争いを当主が裁く仕組みが整えられた。',
    detail: {
      1: '戦国の大名は、自分の国だけできまりを作りました。分国法（ぶんこくほう）といいます。家来どうしのけんかを力ずくで解決させず、大名が裁くようにしたのが大きな特ちょうです。田や水の使い方についてのきまりもありました。',
      2: '戦国大名が領国内にだけ適用される法を定めたものを分国法という。伊達稙宗の『塵芥集』(1536年、171か条)、武田晴信（信玄）の『甲州法度之次第』(1547年)、今川義元の『今川仮名目録追加』(1553年)、結城政勝の『結城氏新法度』(1556年)、六角氏の『六角氏式目』(1567年)などが知られる。共通してみられるのが喧嘩両成敗で、家臣どうしの争いを実力で解決させず、当主の裁定に従わせるものだった。ほかに、他国との無断の音信の禁止、年貢や借銭の扱い、用水の配分、逃散した百姓の帰住など、農村の生活に関わる条文も多い。『六角氏式目』は家臣たちが作った案を当主が承認する形をとっており、大名の権力が一方的でなかったことを示す。',
      3: '戦国大名が領国（分国）にのみ効力を持つ法として制定したものを分国法または家法という。代表的なものに、伊達稙宗『塵芥集』(天文5年・1536年、171か条)、武田晴信『甲州法度之次第』(天文16年・1547年)、今川氏親『今川仮名目録』(大永6年・1526年)とその追加(天文22年・1553年)、結城政勝『結城氏新法度』(弘治2年・1556年)、六角氏『六角氏式目』(永禄10年・1567年)、『大内氏掟書』などがある。中心となる原則は喧嘩両成敗で、私闘の当事者双方を処罰することで自力救済を否定し、紛争の裁定権を当主に集める意図があった。『今川仮名目録追加』は守護使不入の特権を否定し、今川氏の支配が室町幕府の守護としてではなく自らの実力に基づくことを明言している。一方で、これらの法は大名の専制を示すものとばかりは言えない。『甲州法度之次第』には当主自身が法に背いた場合に訴え出てよいとする条文があり、『六角氏式目』は家臣が起草し当主父子が承認する形式をとる。条文には検地・年貢・借銭・用水・山林の入会・欠落（逃散）した百姓の扱い、婚姻や相続、女性の財産に関する規定も含まれ、農村や家の日常を対象としていた。分国法を持たない大名も多く、支配の実態は個別の下知状や置目の集積によっていたことにも注意が要る。',
    },
    year: [1526, 1567],
    keywords: ['分国法', '塵芥集', '甲州法度之次第', '今川仮名目録', '喧嘩両成敗', '検地'],
    caveat:
      '分国法をすべての戦国大名が持っていたわけではなく、確認できるのは十数家に限られます。また「大名が絶対的な権力を握った証拠」とする説明は一面的で、『六角氏式目』のように家臣側が起草した例もあり、当主の権力が家臣団との合意の上に成り立っていたことを示す条文も含まれます。『甲州法度之次第』については、26か条が先で55か条が後の増補とする通説が現在は見直され、55か条を原型とみる説が有力です。',
    gameNote:
      '「軍略+1と札を1枚引く」という効果は、家中の争いを当主が裁けるようになったことを、判断のための資源として置き換えたものです。分国法は本来、出目を操作したり合戦を有利にしたりする性質のものではありません。年を1547年（甲州法度之次第）で代表させていますが、実際には1526年から1567年にかけて各家がそれぞれ制定しており、一斉に広まったわけではありません。',
  },
};

// ---------------------------------------------------------------------------
// 1549 — キリスト教の伝来（時代法）
// ---------------------------------------------------------------------------

/**
 * docs/05 §4-3 に従い、宗教勢力の記述は事実に留める。
 * 布教を「善い／悪い」と評価する効果にはせず、
 * 交易の利と在来の寺社との関係のどちらを取るかの選択にしている。
 */
export const KIRISUTOKYOU_DENRAI: HistoricalEvent = {
  id: 'ev:kirisutokyou-denrai',
  year: 1549,
  chapter: 2,
  kind: 'era-law',
  name: 'キリスト教の伝来',
  reading: 'きりすときょうのでんらい',
  effects: [
    {
      t: 'target',
      who: 'all',
      then: [
        {
          t: 'choice',
          options: [
            {
              label: '南蛮船を迎え、宣教師の滞在を許す（銭+3、威信-1）',
              then: [
                { t: 'gain', res: 'zeni', n: 3 },
                { t: 'lose', res: 'ishin', n: 1 },
              ],
            },
            {
              label: '在来の寺社との縁を保つ（威信+2）',
              then: [{ t: 'gain', res: 'ishin', n: 2 }],
            },
          ],
        },
      ],
    },
  ],
  note: {
    summary:
      '1549年、イエズス会の宣教師フランシスコ・ザビエルが鹿児島に上陸し、日本でのキリスト教の布教が始まった。宣教師の活動は南蛮船の来航と結びついており、貿易の利を求めて布教を許す大名もいた。',
    detail: {
      1: '1549年、フランシスコ・ザビエルという宣教師が鹿児島に来て、日本にキリスト教をつたえました。宣教師たちは学校や病院もつくりました。南蛮船とよばれる外国の船がやってくると品物も入ってくるので、それをのぞんで布教をゆるした大名もいました。',
      2: '天文18年(1549)、イエズス会の宣教師フランシスコ・ザビエルが、日本人ヤジロウ（アンジロー）の案内で鹿児島に上陸した。島津貴久の許可を得て布教を始め、その後平戸・山口・京都を経て、1551年には豊後府内で大友義鎮（宗麟）に迎えられた。ザビエル自身は同年インドへ戻り、翌年中国の上川島で没している。宣教師の来日はポルトガル船の来航と結びついており、大名の中には南蛮貿易の利益を期待して布教を認める者がいた。宣教師は布教のかたわら、府内に病院、各地にセミナリヨ（神学校）を設け、活版印刷の技術も持ち込んだ。一方で、在来の仏教勢力との間には摩擦が生じ、地域や時期によって受け入れ方は大きく異なった。',
      3: '天文18年7月22日（西暦1549年8月15日）、イエズス会士フランシスコ・ザビエルがマラッカで出会った日本人ヤジロウ（アンジロー）を案内役として鹿児島に上陸し、日本におけるキリスト教布教が始まった。ザビエルは守護大名島津貴久から布教の許可を得たが、のちに制限されたため平戸へ移り、山口で大内義隆、豊後府内で大友義鎮に接触した。天文20年(1551)に日本を離れ、翌年中国広東沖の上川島で死去している。布教は南蛮貿易と不可分であった。ポルトガル船の寄港地は宣教師の所在と連動する傾向があり、貿易の利を求めた大名が布教を許容する例が少なくない。大村純忠は1563年に受洗し、1580年に長崎をイエズス会へ寄進している。宣教師側は日本の言語・習慣の研究を進め、府内の病院、有馬・安土のセミナリヨ、豊後のコレジヨを設け、天正遣欧使節の派遣(1582)や活版印刷（キリシタン版）の導入も行った。信徒には武士だけでなく農漁民や女性も多く含まれ、地域共同体単位で受洗する例もあった。一方、既存の仏教・神道勢力との間には教義上・経済上の対立が生じ、寺社の破却が行われた地域もある。1587年の豊臣秀吉によるバテレン追放令、1612年以降の江戸幕府の禁教令へと政策は転じていく。この過程は、信仰・交易・軍需（硝石の輸入）が絡み合った複合的なもので、単純に「布教の成功と挫折」として整理できるものではない。',
    },
    year: 1549,
    keywords: ['キリスト教', 'フランシスコ・ザビエル', 'イエズス会', '南蛮貿易', '大友義鎮', '布教'],
    caveat:
      'キリスト教の受け入れを、大名の信仰心だけで説明することも、貿易目的だけで説明することもできません。史料からは両方の動機が読み取れ、人物や時期によって比重が異なります。また「キリシタン大名」という語は後世の整理であり、当時の呼び方ではありません。宣教師の残した報告は布教の成果を強調する傾向があるため、信徒数などの数字はそのまま実数とは扱えません。',
    gameNote:
      '「南蛮船を迎える」と「在来の寺社との縁を保つ」の二択にし、前者に威信-1を付けていますが、これはキリスト教を受け入れることが劣ることを意味しません。寺社との結びつきが大名の権威の一部だった当時の事情を、盤上の威信という数値に置き換えたものです。実際の選択はこれほど単純ではなく、両方と関係を保った大名も多くいました。また docs/01 §7 では「肥前・豊後が南蛮交易マスに変化」とありますが、Effect 型でマスの種別を変える表現ができないため、全員の選択という形に置き換えています。',
  },
};

// ---------------------------------------------------------------------------
// 1551 — 大寧寺の変と勘合貿易の終わり（事件札）
// ---------------------------------------------------------------------------

/**
 * 下剋上そのものを扱う事件札。grantGekokujo をここで配る。
 * 同時に日明貿易が途絶えることを銭の減少で示す。
 */
export const DAINEIJI_NO_HEN: HistoricalEvent = {
  id: 'ev:daineiji-no-hen',
  year: 1551,
  chapter: 2,
  kind: 'incident',
  name: '大寧寺の変',
  reading: 'たいねいじのへん',
  effects: [
    { t: 'target', who: 'last', then: [{ t: 'grantGekokujo', n: 1 }] },
    { t: 'target', who: 'all', then: [{ t: 'lose', res: 'zeni', n: 1 }] },
  ],
  note: {
    summary:
      '1551年、周防の大内義隆が家臣の陶隆房（晴賢）に背かれ、長門の大寧寺で自害した。大内氏の主導した日明貿易（勘合貿易）は1547年の遣明船を最後に途絶えた。',
    detail: {
      1: '1551年、山口をおさめていた大内義隆が、家来の陶隆房にせめられて亡くなりました。大内氏は中国（明）との貿易をおこなっていましたが、この争いのあと貿易はとだえてしまいました。家来が主人をたおすことを下剋上といいます。',
      2: '天文20年(1551)、周防・長門を中心に西国で大きな勢力を持っていた大内義隆が、重臣の陶隆房（のちの晴賢）の挙兵を受け、長門の大寧寺で自害した。嫡子の義尊も殺され、大内氏の直系は絶えた。陶隆房は豊後の大友氏から大内義長を迎えて当主に立てたが、実権は陶氏が握った。大内氏は室町時代を通じて日明貿易（勘合貿易）を主導しており、1547年に送った4隻が最後の遣明船となった。山口は大内氏のもとで京都の公家や僧が下向する文化の中心地でもあり、この政変はその環境も大きく変えた。1555年の厳島の戦いは、この政変の延長線上に起きている。',
      3: '天文20年8月末から9月1日（西暦1551年9月28日から30日）、周防・長門・石見・豊前などに勢力を持った大内義隆が、重臣陶隆房（のち晴賢）の挙兵により長門深川の大寧寺で自害した。嫡男義尊も殺害され、大内氏の嫡流は断絶する。陶隆房は義隆の甥にあたる大友晴英（大内義長）を豊後から迎えて名目上の当主としたが、政務の実権は陶氏が掌握した。挙兵の名分としては、義隆が文事に傾き武を軽んじたこと、文治派の相良武任を重用したことなどが挙げられるが、これらは軍記類の記述に多くを負っており、実際には出雲遠征（1542-43年）の失敗による家中の亀裂、周防・長門の国人層と重臣層の利害対立が背景にあったとみられる。経済的影響は大きい。大内氏は寧波の乱(1523年)以後、日明貿易の主導権を細川氏から奪って独占していたが、天文16年(1547)に派遣した4隻が最後の遣明船となり、応永11年(1404)以来約150年続いた勘合貿易は終わった。以後、日明間の交易は後期倭寇やポルトガル船を介した私貿易へ移行する。文化面では、山口は応仁の乱後に多くの公家・僧侶・連歌師が下向した都市であり、雪舟が晩年を過ごし、ザビエルも1551年に滞在して布教を行っていた。政変はこの環境を一変させた。陶晴賢は1555年の厳島の戦いで毛利元就に敗れ、大内義長も1557年に滅ぼされて、旧大内領は毛利氏に帰した。',
    },
    year: 1551,
    keywords: ['大寧寺の変', '大内義隆', '陶晴賢', '下剋上', '勘合貿易', '山口'],
    caveat:
      '陶隆房の挙兵の理由を「義隆が文化にふけって政治を怠ったから」と説明することがよくありますが、これは『陰徳太平記』などの後世の軍記に強く依拠した見方です。近年は、出雲遠征の失敗による家中の対立や、重臣層と国人層の利害といった構造的な要因が重視されています。義隆・隆房のどちらかを正しいと評価できる出来事ではありません。',
    gameNote:
      '「最下位のプレイヤーが下剋上札を1枚得る」という効果は、家臣が主君に取って代わる下剋上という語をゲームの逆転装置に結びつけたものです。史実の陶隆房が「最下位の弱者」だったわけではなく、大内家中で最大級の兵力を持つ重臣でした。全員が銭を1失うのは、勘合貿易の途絶を表しています。',
  },
};

// ---------------------------------------------------------------------------
// 1553 — 川中島の戦い（事件札）
// ---------------------------------------------------------------------------

/**
 * docs/01 §7 の「隣接プレイヤーが介入可」は、docs/06 §10 で横槍が
 * 第III章以降に制限されたため、そのままでは実装できない。
 * 代わりに「駒が信濃へ引き寄せられる」「対陣は決着しにくい」で表す。
 */
export const KAWANAKAJIMA: HistoricalEvent = {
  id: 'ev:kawanakajima',
  year: 1553,
  chapter: 2,
  kind: 'incident',
  name: '川中島の戦い',
  reading: 'かわなかじまのたたかい',
  effects: [
    { t: 'moveTo', square: 20 },
    { t: 'holeCardMod', n: 1, scope: 'defend' },
  ],
  note: {
    summary:
      '1553年から1564年にかけて、武田信玄と上杉謙信が北信濃の川中島周辺で数度対陣した。信濃の国人衆の去就と、千曲川・犀川の流域をめぐる争いだった。決着はつかなかった。',
    detail: {
      1: '1553年から1564年まで、武田信玄と上杉謙信は信濃（いまの長野県）の川中島で何度も向かい合いました。合わせて五回といわれます。どちらも相手をたおすことはできず、勝負はつきませんでした。',
      2: '天文22年(1553)から永禄7年(1564)まで、甲斐の武田晴信（信玄）と越後の長尾景虎（上杉謙信）が、北信濃の川中島（千曲川と犀川が合流する一帯、現在の長野市南部）で数度対陣した。武田氏が信濃へ進出して村上義清・小笠原長時らを追ったため、彼らが越後の長尾氏を頼ったことが発端である。一般に五回とされ、うち永禄4年(1561)の第四次が最も大規模な戦闘になったと伝えられる。ただし他の回は対陣のみで大きな戦闘に至らなかったとみられる。結果として川中島以南は武田氏、以北は上杉氏の勢力下に落ち着いた。長期の対陣は双方の農村に兵糧の徴発という負担をかけ、周辺の村々には合戦を避けて山へ逃れた記録も残る。',
      3: '天文22年(1553)から永禄7年(1564)にかけて、甲斐の武田晴信（信玄）と越後の長尾景虎（上杉政虎・輝虎、のち謙信）が北信濃の川中島周辺で対陣した一連の軍事行動。千曲川と犀川の合流する低地一帯（現在の長野市南部）が主な舞台となった。契機は武田氏の信濃侵攻で、村上義清・小笠原長時ら北信の国人が越後へ逃れて長尾氏に支援を求めたことにある。通説では五回（1553・1555・1557・1561・1564）とされるが、この回数の整理自体が後世のもので、実際には小規模な衝突や対陣が断続的に続いた。最大の戦闘とされる永禄4年(1561)の第四次については、武田方が別働隊で妻女山の上杉勢を攻め、下山した上杉勢と八幡原で激突したとする「啄木鳥戦法」の経過が広く知られるが、これは『甲陽軍鑑』に基づく記述であり、同書は江戸初期成立の軍学書として史料的性格に議論がある。両将が単騎で切り結んだとする逸話も同様である。一次史料からは、この年に大規模な戦闘があり武田方で信繁・諸角虎定らが戦死したこと、双方が損害を認めていることが確認できる程度である。結果として国境は千曲川筋でほぼ固定し、以後両者の主戦場は関東と西上野へ移った。長期の対陣は北信の村々に兵糧・人夫の徴発を強い、寺社や集落が避難や還住を繰り返した記録が残る。合戦を語るとき、この地域に暮らした人びとの負担も併せて見る必要がある。',
    },
    year: [1553, 1564],
    keywords: ['川中島', '武田信玄', '上杉謙信', '信濃', '千曲川', '国人'],
    caveat:
      '「五回の合戦」という整理や、第四次の啄木鳥戦法、信玄と謙信の一騎打ちといった有名な場面は、江戸初期成立の『甲陽軍鑑』など後世の記録によるものです。同時代の史料から確実にわかるのは、1561年に大きな戦闘があり双方が損害を受けたことなど限られた事実にとどまります。両者が「宿命のライバル」として繰り返し語られる構図も、軍記物と近代以降の物語を通じて形づくられたものです。',
    gameNote:
      '効果として「駒が信濃(20)へ動く」「この合戦では守り手の役が1段階上がる」を置いています。前者は史実の出兵を盤上の移動に置き換えたもの、後者は五度対陣しても決着がつかなかったことを、守り手有利という形に翻訳したものです。docs/01 §7 には「隣接プレイヤーが介入可」とありますが、横槍（三つ巴）は docs/06 §10 で第III章以降に限られているため、第II章では採用していません。',
  },
};

// ---------------------------------------------------------------------------
// 1555 — 厳島の戦い（事件札）
// ---------------------------------------------------------------------------

/**
 * docs/06 §4 の「寡兵が大軍を破る」を、手勢札の枚数で表す。
 * 首位が1枚減り、最下位が1枚増える。
 */
export const EV_ITSUKUSHIMA: HistoricalEvent = {
  id: 'ev:itsukushima',
  year: 1555,
  chapter: 2,
  kind: 'incident',
  name: '厳島の戦い',
  reading: 'いつくしまのたたかい',
  effects: [
    { t: 'target', who: 'leader', then: [{ t: 'holeCardMod', n: -1 }] },
    { t: 'target', who: 'last', then: [{ t: 'holeCardMod', n: 1 }] },
  ],
  note: {
    summary:
      '1555年、安芸の毛利元就が、大内氏の実権を握っていた陶晴賢の軍を厳島で破った。狭い島に大軍を誘い込んだ戦いとして知られ、毛利氏が中国地方へ広がる転機になった。',
    detail: {
      1: '1555年、安芸（いまの広島県）の毛利元就が、厳島という小さな島で陶晴賢の軍と戦って勝ちました。島はせまいので、大きな軍でも力をいかせませんでした。このあと毛利氏は中国地方で大きな力を持つようになりました。',
      2: '天文24年(1555)、安芸の毛利元就と、大内氏の実権を握っていた陶晴賢が厳島で戦った。厳島は厳島神社のある島で、平地が少なく大軍を展開できない。元就は島に宮尾城を築いて陶軍を誘い、瀬戸内の水軍の協力を得て海上から兵を渡し、陶軍を破った。陶晴賢は島内で自害したと伝えられる。この戦いにより大内氏の勢力は決定的に衰え、毛利氏は周防・長門へ進出して、のちに石見銀山や山陰へも支配を広げた。厳島は古くから信仰を集めた場所で、合戦後には血で汚れた社殿を清める作業が行われたと伝えられる。',
      3: '天文24年10月1日（西暦1555年10月16日。改元は同年10月23日で、慣用的に弘治元年とも表記される）、安芸の国人領主から戦国大名へ成長した毛利元就と、大寧寺の変で大内氏の実権を握った陶晴賢の軍が、安芸国厳島（宮島）で交戦した。元就は島内に宮尾城を築いて挑発し、陶軍を渡海させたうえで、来島村上氏・能島村上氏らの瀬戸内水軍の協力を得て自軍を島へ渡し、退路を断って攻撃したとされる。陶晴賢は島内で自害した。兵力は毛利方4千、陶方2万などと伝えられるが、これらの数値は『陰徳太平記』など後世の軍記に由来し、実数として確認できるものではない。この戦いの意義は、瀬戸内海の制海権を握る水軍勢力の去就が陸戦の帰趨を決めた点にある。村上氏は通行料（駄別料）を徴収し、警固の名目で航行を保障する海上勢力であり、大名に従属していたわけではない。厳島は推古朝の創建を伝える厳島神社の島であり、平清盛の造営以来、瀬戸内の信仰と交易の要でもあった。神域での合戦であったため、戦後に社殿や島の清掃・修祓が行われ、遺体を島外へ移したと伝えられる。毛利氏はこの後、1557年に大内義長を滅ぼして周防・長門を、1562年以降に石見銀山を掌握し、中国地方の広域を支配する大名となった。なお元就の三人の子（隆元・吉川元春・小早川隆景）による体制や「三本の矢」の教訓は、後者は後世の創作とされる。',
    },
    year: 1555,
    keywords: ['厳島の戦い', '毛利元就', '陶晴賢', '村上水軍', '厳島神社', '瀬戸内'],
    caveat:
      '「4千の毛利軍が2万の陶軍を破った」という兵力差は『陰徳太平記』など後世の軍記に基づくもので、確かな数字ではありません。厳島へ誘い出す謀略の詳細（偽の情報を流した、家臣の裏切りを装ったなど）も同様に後世の記述に多くを負っています。毛利元就の「三本の矢」の逸話は史実ではないとされています。合戦当日の和暦は天文24年10月1日で、天文から弘治への改元は同年10月23日（西暦1555年11月7日）のことです。したがって当日はまだ天文年間ですが、この年をまとめて弘治元年と表記する史料や書籍も多くあります。',
    gameNote:
      '天下点1位の手勢札が1枚減り、最下位は1枚増える、という効果にしています。これは「寡兵が大軍を破りうる」という結果を、docs/06 §4 の手勢札の枚数という仕組みに翻訳したものです。史実の毛利元就は最下位の弱小勢力ではなく、安芸の有力国人から着実に勢力を広げた立場でした。また、島の地形と水軍の協力という条件が結果を左右したのであって、少数であること自体が有利だったわけではありません。',
  },
};

// ---------------------------------------------------------------------------
// 1560 — 桶狭間の戦い（事件札・章の締めくくり）
// ---------------------------------------------------------------------------

/**
 * docs/01 §7 では第二章の表に置かれているので、ここに収める。
 * 「奇襲」を効果名に出さず、攻め手の役の段階補正として扱っている。
 * 奇襲説そのものが近年疑問視されているため（caveat 参照）。
 */
export const EV_OKEHAZAMA: HistoricalEvent = {
  id: 'ev:okehazama',
  year: 1560,
  chapter: 2,
  kind: 'incident',
  name: '桶狭間の戦い',
  reading: 'おけはざまのたたかい',
  effects: [
    { t: 'target', who: 'all', then: [{ t: 'draw', n: 1 }] },
    { t: 'holeCardMod', n: 1, scope: 'attack' },
  ],
  note: {
    summary:
      '1560年、尾張の桶狭間で織田信長の軍が今川義元の軍と戦い、義元が討たれた。今川氏の勢力は衰え、松平元康（のちの徳川家康）が三河で自立する契機になった。',
    detail: {
      1: '1560年、いまの愛知県の桶狭間というところで、織田信長の軍と今川義元の軍が戦いました。人数のすくない信長の軍が勝ち、義元はうちとられました。このあと信長の名は広く知られるようになりました。',
      2: '永禄3年(1560)、尾張国桶狭間（現在の愛知県豊明市から名古屋市緑区のあたり）で、駿河・遠江・三河を支配する今川義元の軍と、尾張の織田信長の軍が戦った。兵力は今川方が大きく上回っていたと伝えられるが、信長の軍が義元の本陣を突き、義元は討たれた。これにより今川氏の勢力は急速に衰え、今川方にあった松平元康（のちの徳川家康）が三河で自立する契機となった。信長にとっては領国の危機を脱し、以後の美濃・畿内への進出につながる転機となった。合戦の経過については、長く語られてきた迂回奇襲説が近年見直されている。',
      3: '永禄3年5月19日（西暦1560年6月12日）、尾張国桶狭間で織田信長の軍と今川義元の軍が交戦し、義元が討死した。『信長公記』は今川方を四万五千、織田方を二千ほどと記すが、これも実数として確かめられてはいない。一般に流布する「2万5千対2千」は後世の推計に基づく数値である。経過については「信長が迂回して背後から奇襲した」とする迂回奇襲説が長く通説であった。これは江戸期の『甫庵信長記』や、明治期の陸軍参謀本部『日本戦史』に由来する理解である。しかし藤本正行らの研究により、一次史料に近い『信長公記』を読む限り、信長は桶狭間山に布陣する義元へ正面から攻めかかったと解されることが指摘され、現在は正面攻撃説が有力とされる。今川義元の出兵目的も、かつて言われた「上洛」ではなく尾張への進出とみる見方が強い。合戦地の比定にも豊明市説と名古屋市緑区説がある。義元は駿河・遠江で検地を進め『今川仮名目録追加』を定めた統治者でもあり、「無能な公家かぶれ」とする人物像は後世の軍記や近代の講談によって形づくられたものである。なお、この時期の合戦では周辺の村が兵糧の徴発や放火の被害を受けることが珍しくなく、勝敗の記録の背後にはそうした人びとの生活があった。',
    },
    year: 1560,
    keywords: ['桶狭間', '織田信長', '今川義元', '尾張', '松平元康', '奇襲説'],
    caveat:
      '桶狭間の戦いは長く「信長が迂回して奇襲した」と語られてきましたが、この迂回奇襲説は江戸時代の軍記物や明治期の戦史書に由来するもので、一次史料に近い『信長公記』からは正面から攻めかかったと読めます。近年は正面攻撃説が有力です。兵力についても、『信長公記』は今川方を四万五千、織田方を二千ほどと記す一方、広く知られる「2万5千対2千」は近代以降に広まった推計値で『信長公記』の数字ではありません。いずれも確かな実数ではない点は同じです。今川義元の目的が上洛だったという説明も現在は疑問視されています。合戦地の位置にも複数の説があります。',
    gameNote:
      'docs/01 §7 では「全員に奇襲カードを1枚配布」となっていますが、奇襲説そのものが近年疑問視されているため、効果は「全員が札を1枚引く」「引いた者の攻め手の役が1段階上がる」に留め、効果の名に奇襲を用いていません。役の段階補正は、少数の側にも勝ち目があるという結果を仕組みに翻訳したもので、実際の合戦が偶然だけで決まったという意味ではありません。なお1560年は暦の上では第III章(1560-1581)の初年にあたりますが、docs/01 §7 の第二章の表に置かれているため、本ファイルでは第II章の締めくくりとして収めています。',
  },
};

// ---------------------------------------------------------------------------
// 章のデッキ
// ---------------------------------------------------------------------------

/** 章の開始時に必ず発動する時代法。年代順。 */
export const CHAPTER2_ERA_LAWS: readonly HistoricalEvent[] = [
  HAIFUKIHOU,
  TEPPOU_DENRAI,
  EV_BUNKOKUHOU,
  KIRISUTOKYOU_DENRAI,
];

/** 評定マスで引く事件札。年代順。 */
export const CHAPTER2_INCIDENTS: readonly HistoricalEvent[] = [
  YAMASHINA_HONGANJI,
  DAINEIJI_NO_HEN,
  KAWANAKAJIMA,
  EV_ITSUKUSHIMA,
  EV_OKEHAZAMA,
];

/** 第II章のイベント全件。年表表示と図鑑に使う。 */
export const CHAPTER2_EVENTS: readonly HistoricalEvent[] = [
  YAMASHINA_HONGANJI,
  HAIFUKIHOU,
  TEPPOU_DENRAI,
  EV_BUNKOKUHOU,
  KIRISUTOKYOU_DENRAI,
  DAINEIJI_NO_HEN,
  KAWANAKAJIMA,
  EV_ITSUKUSHIMA,
  EV_OKEHAZAMA,
];
